import { useEffect, useState } from "react";
import { CButton } from "@coreui/react";
import {
  goToPortalV1,
  portalOrigin,
  portalV2Url,
  V2_RELOAD_KEY,
} from "../utils/portal-version";

export { portalOrigin, portalV2Url };

/**
 * CoreUI (v1) does not implement portal v2 routes. On hosted envs, `/v2/*` is
 * served by a Render rewrite to the v2 static site on the same hostname
 * (e.g. dev.service.kingenergy.com/v2). If the v1 SPA mounted anyway, force one
 * full navigation on this origin so the edge rewrite can run.
 *
 * Local dev: use `VITE_V2_DEV_SERVER` in vite.config to proxy `/v2` to the v2
 * dev server on another port.
 */
function isLocalDevHost(): boolean {
  return (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
}

export default function V2PortalRedirect() {
  const [rewriteFailed, setRewriteFailed] = useState(false);

  useEffect(() => {
    if (isLocalDevHost()) {
      return;
    }

    const { pathname, search, hash } = window.location;

    // Render `/v2/*` matches `/v2/` and below, not bare `/v2` — normalize so the
    // rewrite runs instead of v1's /* → index.html catch-all.
    if (pathname === "/v2") {
      window.location.replace(`${portalOrigin()}/v2/${search}${hash}`);
      return;
    }

    // Unhandled /v2/... still on v1: one full reload in case we arrived via client routing.
    if (!sessionStorage.getItem(V2_RELOAD_KEY)) {
      sessionStorage.setItem(V2_RELOAD_KEY, "1");
      window.location.replace(window.location.href);
      return;
    }

    sessionStorage.removeItem(V2_RELOAD_KEY);
    setRewriteFailed(true);
  }, []);

  if (!isLocalDevHost()) {
    if (rewriteFailed) {
      return (
        <div style={{ margin: 20, maxWidth: 520, lineHeight: 1.5 }}>
          <h4>Portal v2</h4>
          <p>
            Expected a Render rewrite from <code>/v2/*</code> to the v2 static
            site on <code>{portalOrigin()}</code>, but this request is still
            being handled by the v1 app.
          </p>
          <p>
            Check that a <code>/v2</code> or <code>/v2/*</code> rewrite to the
            v2 static site is <strong>above</strong> the{" "}
            <code>{"/* → /index.html"}</code> rule. Render&apos;s{" "}
            <code>/v2/*</code> pattern does not match bare <code>/v2</code>{" "}
            (add a rule for <code>/v2</code> → v2 origin, or use{" "}
            <code>/v2/</code>).
          </p>
          {/* Runtime escape when v2 is not deployed: a stored v2 preference would
              otherwise bounce every classic entry route here until v1 is rebuilt
              with VITE_PORTAL_VERSION_AUTO_REDIRECT=false. */}
          <CButton color="primary" onClick={goToPortalV1}>
            Use the classic portal
          </CButton>
        </div>
      );
    }

    return <div style={{ margin: 20 }}>Loading portal v2…</div>;
  }

  return (
    <div style={{ margin: 20, maxWidth: 520, lineHeight: 1.5 }}>
      <h4>Portal v2</h4>
      <p>
        Run the v2 branch on port 3004 with <code>VITE_BASE_PATH=/v2/</code> and
        set <code>VITE_V2_DEV_SERVER=https://localhost:3004</code> in{" "}
        <code>.env</code>, then restart v1.
      </p>
      <p>
        v2 will be available at{" "}
        <a href={portalV2Url()}>{portalV2Url()}</a> via the dev proxy.
      </p>
    </div>
  );
}
